import * as yup from "yup";
import { TFunction } from "i18next";
import { isLessThan12Months } from "src/utils/date";
import { StringSchema } from "yup";
import { validateDOB } from "src/components/dob-input/dob-validation";
import { ADDRESS_TEXT_PATTERN, LOCALITY_PATTERN, POSTCODE_PATTERN } from "src/consts/address-validation";

export const getValidationSchema = (t: TFunction) => {
    const addressFieldValidations = {
        flatNumber: yup
            .string()
            .matches(ADDRESS_TEXT_PATTERN, (params) =>
                t("addressDetails.error.invalidChars2", { fieldName: params.label })
            )
            .label(t("addressDetails.flatNumber")),
        houseNumber: yup
            .string()
            .matches(ADDRESS_TEXT_PATTERN, (params) =>
                t("addressDetails.error.invalidChars2", { fieldName: params.label })
            )
            .label(t("addressDetails.houseNumber")),
        houseName: yup
            .string()
            .matches(ADDRESS_TEXT_PATTERN, (params) =>
                t("addressDetails.error.invalidChars2", { fieldName: params.label })
            )
            .label(t("addressDetails.houseName")),
        street: yup
            .string()
            .matches(ADDRESS_TEXT_PATTERN, (params) =>
                t("addressDetails.error.invalidChars2", { fieldName: params.label })
            )
            .label(t("addressDetails.street")),
        townCity: yup
            .string()
            .matches(LOCALITY_PATTERN, (params) => t("addressDetails.error.invalidChars", { fieldName: params.label }))
            .label(t("addressDetails.townCity")),
        county: yup
            .string()
            .matches(LOCALITY_PATTERN, (params) => t("addressDetails.error.invalidChars", { fieldName: params.label }))
            .label(t("addressDetails.county")),
        postcode: yup
            .string()
            .matches(POSTCODE_PATTERN, (params) => t("addressDetails.error.invalidChars1", { fieldName: params.label }))
            .label(t("addressDetails.postcode")),
        month: yup.string().label(t("addressDetails.month")),
        year: yup.string().label(t("addressDetails.year")),
    };

    const addressSchema = yup.object().shape(
        {
            flatNumber: addressFieldValidations.flatNumber.when(["houseNumber", "houseName"], {
                is: (houseNumber: string, houseName: string) => houseNumber === "" && houseName === "",
                then: (schema: StringSchema) =>
                    schema.required(t("addressDetails.error.flatOrHouseNumberOrNameRequired")),
                otherwise: (schema: StringSchema) => schema.nullable(),
            }),
            houseNumber: addressFieldValidations.houseNumber.when(["flatNumber", "houseName"], {
                is: (flatNumber: string, houseName: string) => flatNumber === "" && houseName === "",
                then: (schema: StringSchema) =>
                    schema.required(t("addressDetails.error.flatOrHouseNumberOrNameRequired")),
                otherwise: (schema: StringSchema) => schema.nullable(),
            }),
            houseName: addressFieldValidations.houseName.when(["flatNumber", "houseNumber"], {
                is: (flatNumber: string, houseNumber: string) => flatNumber === "" && houseNumber === "",
                then: (schema: StringSchema) =>
                    schema.required(t("addressDetails.error.flatOrHouseNumberOrNameRequired")),
                otherwise: (schema: StringSchema) => schema.nullable(),
            }),
            street: addressFieldValidations.street.required(t("addressDetails.error.streetRequired")),
            townCity: addressFieldValidations.townCity.required(t("addressDetails.error.townCityRequired")),
            county: addressFieldValidations.county.required(t("addressDetails.error.countyRequired")),
            postcode: addressFieldValidations.postcode.required(t("addressDetails.error.postcodeRequired")),
            month: addressFieldValidations.month.required(t("addressDetails.error.monthRequired")),
            year: addressFieldValidations.year.required(t("addressDetails.error.yearRequired")),
        },
        [
            ["flatNumber", "houseNumber"],
            ["flatNumber", "houseName"],
            ["houseNumber", "houseName"],
        ]
    );

    return yup.object().shape({
        firstName: yup
            .string()
            .required(t("personalDetails.error.firstNameRequired"))
            .matches(/^[a-zA-Z '-]+$/, (params) => t("personalDetails.error.invalidChars", { fieldName: params.label }))
            .label(t("personalDetails.labels.firstName")),
        lastName: yup
            .string()
            .required(t("personalDetails.error.lastNameRequired"))
            .matches(/^[a-zA-Z '-]+$/, (params) => t("personalDetails.error.invalidChars", { fieldName: params.label }))
            .label(t("personalDetails.labels.lastName")),
        email: yup
            .string()
            .email(t("personalDetails.error.emailInvalid"))
            .required(t("personalDetails.error.emailRequired"))
            .matches(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, () => t("personalDetails.error.emailInvalid"))
            .label(t("personalDetails.labels.email")),
        dobDay: yup.string().required("").label(t("personalDetails.labels.dobDay")),
        dobMonth: yup.string().required("").label(t("personalDetails.labels.dobMonth")),
        dobYear: yup.string().required("").label(t("personalDetails.labels.dobYear")),
        dobCombined: yup.string().test("dob-validation", "", function () {
            const { dobDay, dobMonth, dobYear } = this.parent;
            return validateDOB(dobDay, dobMonth, dobYear, t, this);
        }),
        income: yup
            .string()
            .typeError(t("personalDetails.error.mustBeANumber"))
            .required(t("personalDetails.error.incomeRequired"))
            .label(t("personalDetails.labels.income")),
        currentAddress: addressSchema,
        previousAddress: yup
            .object()
            .when(["currentAddress.month", "currentAddress.year"], ([month, year]) =>
                isLessThan12Months(month, year) ? addressSchema : yup.object().shape({})
            ),
    });
};
